import * as LZString from "lz-string"

import { getCurrentLang, Lang, S, Transations, trySetCurrentLang } from "./strings"

// 'ts-ignore' works on the next line, so this does it for the first line
// @ts-ignore

import imgGab from '../img/gab.svg'; // @ts-ignore
import imgGlo from '../img/glo.svg'; // @ts-ignore
import imgJpp from '../img/jpp.svg'; // @ts-ignore
import imgMel from '../img/mel.svg'; // @ts-ignore

void 0 // dummy line to consume the last 'ts-ignore'

type Point = { x: number, y: number }
type SizeAndCenter = { w: number, h: number, relativeCenter: Point }
type ObjDef = SizeAndCenter & { makeSvg: (this: SizeAndCenter) => SVGElement, hidden?: boolean }

const PoseCodes = [
    "stack",
    "bottom-align-left", "bottom-align-right", "bottom-align-center", "bottom-align-junction",
    "right-align-top", "right-align-bottom", "right-align-center", "right-align-junction"
] as const

type PoseCode = typeof PoseCodes[number]

const AllDialects: Record<string, Dialect> = {}

type ObjectProp = SizeAndCenter & ObjDef

class Dialect {
    constructor(
        public readonly name: string,
        public readonly aliases: string[],
        public readonly objectProps: Record<string, ObjectProp>,
        public readonly emptyObj: string,
        public readonly precolored: boolean,
        public readonly mkPreviewString: (obj: string) => string,
        public readonly poseProps: Record<string, { poseCode: PoseCode, primary?: boolean, invertArgs?: boolean }>,
        public readonly colorProps: Record<string, string> = {},
        public readonly rotation: boolean | Record<string, number> = false,
        public readonly sizeProps: Record<string, number> = {},
    ) {
        this.allObjects = Object.keys(objectProps)
        this.allPoses = Object.keys(poseProps)
        this.allColors = Object.keys(colorProps)
        this.allSizes = Object.keys(sizeProps)
        AllDialects[name] = this
        for (const alias of aliases) {
            AllDialects[alias] = this
        }
    }

    public readonly allObjects: string[]
    public readonly allPoses: string[]
    public readonly allColors: string[]
    public readonly allSizes: string[]

    public copyWith(name: string, aliases: string[], props: Partial<Dialect>): Dialect {
        return new Dialect(
            name,
            aliases,
            props.objectProps ?? this.objectProps,
            props.emptyObj ?? this.emptyObj,
            props.precolored ?? this.precolored,
            props.mkPreviewString ?? this.mkPreviewString,
            props.poseProps ?? this.poseProps,
            props.colorProps ?? this.colorProps,
            props.rotation ?? this.rotation,
            props.sizeProps ?? this.sizeProps
        )
    }
}

const SquareProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        return makeSvgElem("rect", { width: this.w, height: this.h })
    }
}

const RectProp: ObjectProp = {
    w: 3, h: 10, relativeCenter: { x: 1.5, y: 5 },
    makeSvg() {
        return makeSvgElem("rect", { width: this.w, height: this.h })
    }
}

const TriangleProp: ObjectProp = {
    w: 10, h: 8.7, relativeCenter: { x: 5, y: 5.8 },
    makeSvg() {
        return makeSvgPolygon([{ x: 0, y: this.h }, { x: this.w, y: this.h }, { x: this.w / 2, y: 0 }])
    }
}

const CircleProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        return makeSvgElem("ellipse", { cx: this.w / 2, cy: this.h / 2, rx: this.w / 2, ry: this.h / 2, })
    }
}

const OvalProp: ObjectProp = {
    w: 5, h: 10, relativeCenter: { x: 2.5, y: 5 },
    makeSvg() {
        return makeSvgElem("ellipse", { cx: this.w / 2, cy: this.h / 2, rx: this.w / 2, ry: this.h / 2, })
    }
}

const CrossProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        const thickness = 1 / 3.15
        const x1 = this.w * thickness
        const x2 = this.w * (1 - thickness)
        const y1 = this.h * thickness
        const y2 = this.h * (1 - thickness)
        return makeSvgPolygon([{ x: x1, y: 0 }, { x: x1, y: y1 }, { x: 0, y: y1 }, { x: 0, y: y2 }, { x: x1, y: y2 }, { x: x1, y: this.h }, { x: x2, y: this.h }, { x: x2, y: y2 }, { x: this.w, y: y2 }, { x: this.w, y: y1 }, { x: x2, y: y1 }, { x: x2, y: 0 }])
    }
}

const HalfcircleProp: ObjectProp = {
    w: 10, h: 5, relativeCenter: { x: 5, y: 2.5 },
    makeSvg() {
        return makeSvgElem("path", { d: `M 0 ${this.h} A ${this.w / 2} ${this.h} 0 0 1 ${this.w} ${this.h} L ${this.w} ${this.h} L 0 ${this.h} Z` })
    }
}

const SpearheadProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        return makeSvgPolygon([
            { x: this.w / 2, y: 0 },
            { x: this.w, y: this.h },
            { x: this.w / 2, y: this.h / 2 },
            { x: 0, y: this.h }])
    }
}

const StarProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        const outerR = this.w / 2
        const innerR = outerR * 0.382
        const points: Point[] = []
        for (let i = 0; i < 10; i++) {
            const angle = -Math.PI / 2 + i * Math.PI / 5
            const radius = i % 2 === 0 ? outerR : innerR
            points.push({
                x: this.relativeCenter.x + radius * Math.cos(angle),
                y: this.relativeCenter.y + radius * Math.sin(angle),
            })
        }
        return makeSvgPolygon(points)
    }
}

const EmptyProp: ObjectProp = {
    w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
    makeSvg() {
        return makeSvgEmpty()
    },
    hidden: true,
}

export const DialectDefaultEn = new Dialect(
    "default-en",
    [],
    {
        "sqa": SquareProp,
        "rec": RectProp,
        "tri": TriangleProp,
        "cir": CircleProp,
        "ova": OvalProp,
        "cro": CrossProp,
        "haf": HalfcircleProp,
        "spe": SpearheadProp,
        "sta": StarProp,
        "emp": EmptyProp,
    },
    "emp",
    false,
    code => code + "bla",
    {
        "pil": { primary: true, poseCode: "stack" },
        "bel": { primary: true, poseCode: "bottom-align-center" },
        "nex": { primary: true, poseCode: "right-align-center" },
    },
    {
        "yel": "yellow",
        "blu": "blue",
        "cya": "cyan",
        "gre": "green",
        "ora": "orange",
        "pin": "pink",
        "gra": "gray",
        "red": "red",
        "whi": "white",
        "bla": "black",
        "mag": "magenta",
        "bei": "beige",
        "bro": "rgb(128, 79, 0)",
        "vio": "indigo",
    },
    {
        "0": 0,
        "3": 90,
        "6": 180,
        "9": 270,
    },
    {
        "–": 1 / 2,
        "+": 2,
    },
)


export const DialectDefault = new Dialect(
    "default",
    [],
    {
        "car": SquareProp,
        "rec": RectProp,
        "tri": TriangleProp,
        "ron": CircleProp,
        "ova": OvalProp,
        "cro": CrossProp,
        "dem": HalfcircleProp,
        "fer": SpearheadProp,
        "eto": StarProp,
        "vid": EmptyProp,
        "glo": {
            w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
            makeSvg() {
                return makeEmbeddedSvgWith(imgGlo, this.w, this.h)
            },
            hidden: true,
        },
        "mel": {
            w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
            makeSvg() {
                return makeEmbeddedSvgWith(imgMel, this.w, this.h)
            },
            hidden: true,
        },
        "gab": {
            w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
            makeSvg() {
                return makeEmbeddedSvgWith(imgGab, this.w, this.h)
            },
            hidden: true,
        },
        "jpp": {
            w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
            makeSvg() {
                return makeEmbeddedSvgWith(imgJpp, this.w, this.h)
            },
            hidden: true,
        }
    },
    "vid",
    false,
    code => code + "noi",
    {
        "emp": { primary: true, poseCode: "stack" },

        "sou": { primary: true, poseCode: "bottom-align-center" },
        "sod": { poseCode: "bottom-align-right" },
        "sog": { poseCode: "bottom-align-left" },
        "soj": { poseCode: "bottom-align-junction" },

        "cot": { primary: true, poseCode: "right-align-center" },
        "coh": { poseCode: "right-align-top" },
        "cob": { poseCode: "right-align-bottom" },
        "coj": { poseCode: "right-align-junction" },
    },
    {
        "jau": "yellow",
        "ble": "blue",
        "cya": "cyan",
        "ver": "green",
        "ora": "orange",
        "ros": "pink",
        "gri": "gray",
        "rou": "red",
        "bla": "white",
        "noi": "black",
        "mag": "magenta",
        "bei": "beige",
        "bru": "rgb(128, 79, 0)",
        "vio": "indigo",
    },
    {
        "0": 0,
        "3": 90,
        "6": 180,
        "9": 270,
    },
    {
        "–": 1 / 2,
        "+": 2,
    },
)


export const DialectDefaultHours =
    DialectDefault.copyWith(
        "default-angles", [], {
        rotation: true,
    })

export const DialectHivobu = new Dialect(
    "hivobu", [],
    {
        "rah": {
            w: 3, h: 10, relativeCenter: { x: 1.5, y: 5 },
            makeSvg() {
                return makeSvgElem("rect", { width: this.w, height: this.h, "--fillcolor": "#2d6060" })
            }
        },
        "teh": {
            w: 10, h: 8.7, relativeCenter: { x: 5, y: 5.8 },
            makeSvg() {
                return makeSvgPolygon([{ x: 0, y: this.h }, { x: this.w, y: this.h }, { x: this.w / 2, y: 0 }], { "--fillcolor": "#ffff00" })
            }
        },
        "oh": {
            w: 8, h: 8, relativeCenter: { x: 4, y: 4 },
            makeSvg() {
                return makeSvgElem("ellipse", { cx: this.w / 2, cy: this.h / 2, rx: this.w / 2, ry: this.h / 2, "--fillcolor": "#7EB4F9" })
            }
        },
        "vid": {
            w: 10, h: 10, relativeCenter: { x: 5, y: 5 },
            makeSvg() {
                return makeSvgEmpty()
            },
            hidden: true,
        },
    },
    "vid",
    true,
    code => code,
    {
        "co": { poseCode: "stack", primary: true, invertArgs: true },
        "du": { poseCode: "bottom-align-junction", primary: true },
    },
)

type ASTCommon = {
    dialect: Dialect,
    token: string,
    srcElem: HTMLElement,
    builtObjElem: HTMLElement
    cachedSize?: SizeAndCenter,
    svgElems: SVGElement[],
}

type AST = (
    | { _type: 'obj' }
    | { _type: 'pose'; first: AST; second: AST }
    | { _type: 'col'; arg: AST }
    | { _type: 'rot'; arg: AST }
    | { _type: 'size'; arg: AST }
    | { _type: 'call'; def: AST }
) & ASTCommon

type ASTType = AST["_type"]

const CSTOnlyTypes = ["noop", "colchar"] as const
type CSTOnlyType = typeof CSTOnlyTypes[number]

function isCSTOnlyType(type: string): type is CSTOnlyType {
    return (CSTOnlyTypes as readonly string[]).includes(type)
}

type Arity<T extends ASTType | CSTOnlyType> =
    T extends 'obj' | 'call' ? 0 :
    T extends 'pose' ? 2 :
    T extends 'col' | 'rot' | 'size' ? 1 :
    T extends CSTOnlyType ? 0 :
    never

const NoOps = [" ", " ", "(", ")"] as const

class ParseError extends Error {
    constructor(public readonly pos: number, message: string) {
        super(message)
        this.name = "ParseError"
    }
}

function isAST(obj: boolean | AST | undefined): obj is AST {
    return obj !== undefined && typeof obj === "object" && "_type" in obj
}

type ParsedExpr = { type: "full", ast: AST } | { type: "partial", asts: AST[] }

type ParseResult = {
    /**
     * Either "full" if the entire input was parsed successfully, or "partial"
     * if some end operators are missing and we still have a list of objects
     * that have not been put together yet.
     */
    asts: ParsedExpr,
    htmlElements: HTMLElement[],
    elemToAstMap: WeakMap<HTMLElement, AST>,
}

function parse(userCode: string, dialect: Dialect, showPartialInput: boolean): ParseResult {

    const htmlElements: HTMLElement[] = []
    const elemToAstMap: WeakMap<HTMLElement, AST> = new WeakMap()
    const lines = userCode.toLowerCase().replace(/-/g, "–").split(/\r?\n|;/)
    const defs: Map<string, AST> = new Map()
    const stack: AST[] = []

    let l = 0
    let c = 0
    let prevLinesOffset = 0
    let currentLine: string

    const error = (message: string) => {
        throw new ParseError(prevLinesOffset + c, message)
    }

    const tryMatchFrom = <T extends ASTType | CSTOnlyType>(candidates: Iterable<string>, type: T, arity: Arity<T>, opts?: { longest?: boolean, mustMatch?: boolean, prepend?: string, argOrderDict?: Record<string, { invertArgs?: boolean }> }): undefined | true | AST & { _type: T } => {

        let token: string | undefined = undefined
        if (opts?.longest) {
            let best = ""
            for (const candidate of candidates) {
                if (currentLine.startsWith(candidate, c) && candidate.length > best.length) {
                    best = candidate
                }
            }
            if (best.length !== 0) {
                token = best
            }
        } else {
            for (const candidate of candidates) {
                if (currentLine.startsWith(candidate, c)) {
                    token = candidate
                    break
                }
            }
        }

        if (token !== undefined) {
            // console.log(`At pos ${prevLinesOffset + c}, matched ${type} token '${match}'`)
            c += token.length
            // const range = { pos: prevLinesOffset + c, len: match.length }
            const srcElem = document.createElement("span")
            srcElem.classList.add("ast", type)
            srcElem.innerHTML = (opts?.prepend ?? "") + capitalize(token.replace(/\n/g, "<br>"))
            if (isCSTOnlyType(type)) {
                return true
            }
            const argsObj: { arg?: AST, first?: AST, second?: AST } = {}
            let builtObjElem: HTMLElement = srcElem
            if (arity === 0) {
                builtObjElem = srcElem
            } else {
                if (stack.length < arity) {
                    return error(`Not enough operands for type ${type}, expected ${arity}, got ${stack.length}`)
                }
                if (arity === 1) {
                    const arg = stack.pop()!
                    argsObj.arg = arg
                    builtObjElem = document.createElement("span")
                    builtObjElem.classList.add("ast")
                    builtObjElem.appendChild(arg.builtObjElem)
                    builtObjElem.appendChild(srcElem)
                } else if (arity === 2) {
                    const cstSecond = stack.pop()!
                    const cstFirst = stack.pop()!
                    const invertArgs = opts?.argOrderDict && token in opts.argOrderDict && opts.argOrderDict[token].invertArgs
                    // console.log("argOrderDict", opts?.argOrderDict)
                    if (invertArgs) {
                        argsObj.second = cstFirst
                        argsObj.first = cstSecond
                    } else {
                        argsObj.second = cstSecond
                        argsObj.first = cstFirst
                    }
                    builtObjElem = document.createElement("span")
                    builtObjElem.classList.add("ast")
                    builtObjElem.appendChild(cstFirst.builtObjElem)
                    builtObjElem.appendChild(cstSecond.builtObjElem)
                    builtObjElem.appendChild(srcElem)
                } else {
                    return error(`Unsupported arity ${arity} for type ${type}`)
                }
            }
            const common: ASTCommon = { token, srcElem, dialect, builtObjElem, svgElems: [] }
            const ast: AST & { _type: T } = { _type: type, ...argsObj, ...common } as any
            stack.push(ast)
            elemToAstMap.set(srcElem, ast)
            return ast
        }

        if (opts?.mustMatch) {
            return error(`Expected one of ${[...candidates].join(", ")} at position ${c}, got: ${currentLine.slice(c)}`)
        }
        return undefined
    }

    const parseExpr = (inDef: boolean): ParsedExpr => {
        let ast
        while (c < currentLine.length) {
            if (tryMatchFrom(NoOps, "noop", 0))
                continue

            if (isAST(ast = tryMatchFrom(defs.keys(), "call", 0, { longest: true }))) {
                ast.def = defs.get(ast.token)!
                continue
            }

            if (tryMatchFrom(dialect.allObjects, "obj", 0))
                continue

            if (tryMatchFrom(dialect.allColors, "col", 1))
                continue

            if (tryMatchFrom(dialect.allPoses, "pose", 2, { argOrderDict: dialect.poseProps }))
                continue

            if (dialect.rotation !== false && dialect.rotation !== true) {
                if (tryMatchFrom(Object.keys(dialect.rotation), "rot", 1))
                    continue
            } else if (dialect.rotation === true) {
                const rotationMatch = currentLine.slice(c).match(/^\d+/)
                if (rotationMatch && tryMatchFrom([rotationMatch[0]], "rot", 1, { mustMatch: true })) {
                    continue
                }
            }

            if (tryMatchFrom(dialect.allSizes, "size", 1))
                continue

            return error(`Unknown token at position ${c}: ${currentLine.slice(c)}`)
        }

        if (inDef) {
            // definition after the = sign, should always be complete
            if (stack.length !== 1) {
                return error(`Invalid definition, expected exactly 1 item on stack at the end of the line, got ${stack.length}: ${stack.map(ast => String(ast)).join(", ")}`)
            }
            return { type: "full", ast: stack.pop()! }

        } else {
            // main expression
            switch (stack.length) {
                case 0: return error(`Invalid input, expected 1 item on stack, got 0`)
                case 1: return { type: "full", ast: stack[0] }
                default:
                    if (showPartialInput) {
                        return { type: "partial", asts: stack }
                    } else {
                        return error(`Invalid input, expected 1 item on stack, got ${stack.length}: ${stack.map(ast => pretty(ast)).join(", ")}`)
                    }
            }
        }
    }

    const parseDef = (): void => {
        let [nameRaw, exprRaw] = currentLine.split("=")
        if (!nameRaw || !exprRaw) {
            return error(`Invalid definition, expected format "name = expression"`)
        }
        const name = nameRaw.trim().toLowerCase()

        const nameSpan = document.createElement("span")
        nameSpan.classList.add("ast", "obj")
        nameSpan.textContent = capitalize(name)

        const equalsSpan = document.createElement("span")
        equalsSpan.classList.add("ast", "assign")
        equalsSpan.textContent = "="

        if (defs.has(name)) {
            return error(`Duplicate definition for ${name}`)
        }
        c += nameRaw.length + 1

        const parsedDefinition = parseExpr(true)
        if (parsedDefinition.type === "partial") {
            return error(`Incomplete definition for ${name}; asts: ${parsedDefinition.asts.map(ast => String(ast)).join(", ")}`)
        }
        const definition = parsedDefinition.ast
        defs.set(name, definition)

        const defSpan = document.createElement("span")
        defSpan.classList.add("ast", "def")
        defSpan.appendChild(nameSpan)
        defSpan.appendChild(equalsSpan)
        defSpan.appendChild(definition.builtObjElem)
        htmlElements.push(defSpan)
        htmlElements.push(document.createElement("br"))
    }

    for (; l < lines.length; l++) {
        currentLine = lines[l]
        c = 0
        if (currentLine.length !== 0 && !currentLine.startsWith("#")) {


            if (currentLine.includes("=")) {
                parseDef()
            } else {
                const parsedExpr = parseExpr(false)
                const asts = parsedExpr.type === "full" ? [parsedExpr.ast] : parsedExpr.asts
                for (const ast of asts) {
                    htmlElements.push(ast.builtObjElem)
                }
                return { asts: parsedExpr, htmlElements, elemToAstMap }
            }
        }
        prevLinesOffset += currentLine.length + 1
    }

    // no expression; generate empty elem
    const emptySpan = document.createElement("span")
    htmlElements.push(emptySpan)
    const emptyAst: AST = { _type: "obj", token: dialect.emptyObj, srcElem: emptySpan, builtObjElem: emptySpan, dialect, svgElems: [] }

    return { asts: { type: "full", ast: emptyAst }, htmlElements, elemToAstMap }
}

function fallbackParse(input: string): HTMLElement {
    const lines = input.split(/\r?\n|;/)
    const containerSpan = document.createElement("span")
    const push = (token: string) => {
        const tokenSpan = document.createElement("span")
        tokenSpan.classList.add("ast", "fallback")
        tokenSpan.textContent = token
        containerSpan.appendChild(tokenSpan)
    }
    for (let l = 0; l < lines.length; l++) {
        const line = lines[l]

        if (line.length === 0) continue

        enum BufferContentType {
            Str,
            Num,
            Sym,
            Whi,
        }

        let buf = ""
        let bufContentType: BufferContentType | null = null
        const flushBuf = () => {
            if (buf.length > 0) {
                push(buf)
                buf = ""
            }
        }
        for (let c = 0; c < line.length; c++) {
            const char = line[c]
            const isLower = char >= "a" && char <= "z"
            const isWhitespace = char === " " || char === "\t"
            const isSymbol = char === "="
            const isDigit = char >= "0" && char <= "9"
            if (buf.length === 3 || !(isLower || isWhitespace) || isSymbol || bufContentType !== BufferContentType.Str) {
                flushBuf()
            }
            buf += char
            bufContentType = isSymbol ? BufferContentType.Sym : BufferContentType.Str
        }

        flushBuf()

        if (l < lines.length - 1) {
            containerSpan.appendChild(document.createElement("br"))
        }
    }

    console.log("Fallback parsed input:", containerSpan.outerHTML)
    return containerSpan
}

function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1)
}

function rotationTokenToDegrees(dialect: Dialect, token: string): number {
    if (dialect.rotation === true) {
        const angleNum = Number.parseInt(token, 10)
        if (!Number.isFinite(angleNum)) {
            throw new Error(`Invalid rotation token '${token}' for free-angle dialect`)
        }
        return angleNum
    }

    if (dialect.rotation && token in dialect.rotation) {
        return dialect.rotation[token]
    }
    throw new Error(`Unknown rotation token '${token}' for dialect ${dialect.name}`)
}

function normalizeDegrees(angleDegrees: number): number {
    return ((angleDegrees % 360) + 360) % 360
}

function rotationDegreesToToken(dialect: Dialect, angleDegrees: number): string | undefined {
    if (dialect.rotation === true || dialect.rotation === false) {
        return undefined
    }
    const normalized = normalizeDegrees(angleDegrees)
    for (const [token, degrees] of Object.entries(dialect.rotation)) {
        if (normalizeDegrees(degrees) === normalized) {
            return token
        }
    }
    return undefined
}

function rotatePoint(point: Point, angleDegrees: number): Point {
    const angleRadians = angleDegrees * Math.PI / 180
    const cos = Math.cos(angleRadians)
    const sin = Math.sin(angleRadians)
    return {
        x: point.x * cos - point.y * sin,
        y: point.x * sin + point.y * cos,
    }
}

function pretty(ast: AST, parensIfComplex?: boolean): string {
    switch (ast._type) {
        case 'obj':
        case 'call':
            return `<span style="">${capitalize(ast.token)}</span>`
        case 'pose': {
            const invertArgs = ast.dialect.poseProps[ast.token].invertArgs
            const [first, second] = invertArgs ? [ast.second, ast.first] : [ast.first, ast.second]
            const repr = `${pretty(first, true)} ${pretty(second, true)} <span style="font-weight: bold;">${capitalize(ast.token)}</span>`
            if (parensIfComplex) return `(${repr})`
            return repr
        }
        case 'rot': {
            let angleNum = rotationTokenToDegrees(ast.dialect, ast.token)
            let child = ast.arg
            while (child._type === 'rot') {
                angleNum += rotationTokenToDegrees(child.dialect, child.token)
                child = child.arg
            }
            angleNum = normalizeDegrees(angleNum)
            if (rotationTokenToDegrees(ast.dialect, ast.token) === 0) return pretty(child)
            const angleToken = rotationDegreesToToken(ast.dialect, angleNum)
            return `${pretty(child, true)}${angleToken ?? angleNum}`
        }
        case 'size':
            return `${pretty(ast.arg, true)}<span style="font-weight: bold;">${ast.token}</span>`
        case 'col': {
            const cssColor = ast.dialect.colorProps[ast.token]
            return `${pretty(ast.arg, true)}<span style="font-style: italic; padding: 0 0.2ex 0 0.1ex; margin-left: 0.1ex; border: 3px solid ${cssColor}">${capitalize(ast.token)}</span>`
        }
    }
}


function sizeOf(ast: AST): SizeAndCenter {
    if (!ast.cachedSize) {
        ast.cachedSize = ((): SizeAndCenter => {
            switch (ast._type) {
                case 'call':
                    return sizeOf(ast.def)
                case 'obj':
                    return ast.dialect.objectProps[ast.token]
                case 'pose': {
                    const poseCode = ast.dialect.poseProps[ast.token].poseCode
                    const firstSize = sizeOf(ast.first)
                    const secondSize = sizeOf(ast.second)
                    const newWidthDefault = Math.max(firstSize.relativeCenter.x, secondSize.relativeCenter.x) + Math.max(firstSize.w - firstSize.relativeCenter.x, secondSize.w - secondSize.relativeCenter.x)
                    const newHeightDefault = Math.max(firstSize.relativeCenter.y, secondSize.relativeCenter.y) + Math.max(firstSize.h - firstSize.relativeCenter.y, secondSize.h - secondSize.relativeCenter.y)
                    switch (poseCode) {
                        case 'stack':
                            return {
                                w: newWidthDefault,
                                h: newHeightDefault,
                                relativeCenter: {
                                    x: Math.max(firstSize.relativeCenter.x, secondSize.relativeCenter.x),
                                    y: Math.max(firstSize.relativeCenter.y, secondSize.relativeCenter.y)
                                }
                            }
                        case 'bottom-align-left':
                        case 'bottom-align-right':
                        case 'bottom-align-junction': {
                            const relativeCenterX = (() => {
                                switch (poseCode) {
                                    case 'bottom-align-junction': return Math.max(firstSize.relativeCenter.x, secondSize.relativeCenter.x)
                                    case 'bottom-align-left': return Math.min(firstSize.relativeCenter.x, secondSize.relativeCenter.x)
                                    case 'bottom-align-right': return firstSize.w > secondSize.w
                                        ? firstSize.w - (secondSize.w - secondSize.relativeCenter.x)
                                        : secondSize.w - (firstSize.w - firstSize.relativeCenter.x)
                                }
                            })()
                            return {
                                w: newWidthDefault,
                                h: firstSize.h + secondSize.h,
                                relativeCenter: {
                                    x: relativeCenterX,
                                    y: firstSize.h,
                                }
                            }
                        }
                        case "bottom-align-center": {
                            const h = firstSize.h + secondSize.h
                            const w = Math.max(firstSize.w, secondSize.w)
                            // TODO relativeCenter is wrong, it should still indicate the proper junction for possible other junction-based ops
                            return { w, h, relativeCenter: { x: w / 2, y: h / 2 } }
                        }

                        case "right-align-top":
                        case "right-align-bottom":
                        case "right-align-junction":
                            return {
                                w: firstSize.w + secondSize.w,
                                h: newHeightDefault,
                                relativeCenter: {
                                    x: firstSize.w,
                                    y: Math.max(firstSize.relativeCenter.y, secondSize.relativeCenter.y)
                                }
                            }
                        case "right-align-center": {
                            const h = Math.max(firstSize.h, secondSize.h)
                            const w = firstSize.w + secondSize.w
                            // TODO relativeCenter is wrong, it should still indicate the proper junction for possible other junction-based ops
                            return { w, h, relativeCenter: { x: w / 2, y: h / 2 } }
                        }
                    }
                }
                case 'rot': {
                    const angle = rotationTokenToDegrees(ast.dialect, ast.token)
                    const childSize = sizeOf(ast.arg)
                    if (normalizeDegrees(angle) === 0) return childSize
                    const corners: Point[] = [
                        { x: -childSize.relativeCenter.x, y: -childSize.relativeCenter.y },
                        { x: childSize.w - childSize.relativeCenter.x, y: -childSize.relativeCenter.y },
                        { x: -childSize.relativeCenter.x, y: childSize.h - childSize.relativeCenter.y },
                        { x: childSize.w - childSize.relativeCenter.x, y: childSize.h - childSize.relativeCenter.y },
                    ].map(point => rotatePoint(point, angle))
                    const xs = corners.map(point => point.x)
                    const ys = corners.map(point => point.y)
                    const minX = Math.min(...xs)
                    const maxX = Math.max(...xs)
                    const minY = Math.min(...ys)
                    const maxY = Math.max(...ys)
                    return {
                        w: maxX - minX,
                        h: maxY - minY,
                        relativeCenter: {
                            x: -minX,
                            y: -minY
                        }
                    }
                }
                case 'size': {
                    const size = ast.token
                    const childSize = sizeOf(ast.arg)
                    const factor = ast.dialect.sizeProps[size]
                    return {
                        w: childSize.w * factor,
                        h: childSize.h * factor,
                        relativeCenter: {
                            x: childSize.relativeCenter.x * factor,
                            y: childSize.relativeCenter.y * factor
                        }
                    }
                }
                case 'col':
                    return sizeOf(ast.arg)
            }
        })()
    }
    return ast.cachedSize
}


const background = "#f0f0f0"
const darkerBackground = "#dddddd"

function makeSvgElem<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number | undefined>): SVGElementTagNameMap[K] {
    const elem = document.createElementNS("http://www.w3.org/2000/svg", tag)
    for (const [key, value] of Object.entries(attrs)) {
        if (value !== undefined) {
            if (key.startsWith("--")) {
                elem.style.setProperty(key, value.toString())
            } else {
                elem.setAttribute(key, value.toString())
            }
        }
    }
    return elem
}

function setStyle(elem: HTMLElement, styles: Partial<Record<keyof CSSStyleDeclaration, string>>): void {
    Object.assign(elem.style, styles)
}


function makeSvgPolygon(pointsArr: Point[], attrs: Record<string, string | number | undefined> = {}): SVGPolygonElement {
    const points = pointsArr.map(p => `${p.x},${p.y}`).join(" ")
    return makeSvgElem("polygon", {
        points,
        ...attrs
    })
}


function makeSvgEmpty() {
    return makeSvgElem("g", {})
}

function makeEmbeddedSvgWith(svgCode: string, w: number, h: number): SVGElement {

    const href = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgCode)}`
    const image = makeSvgElem("image", {
        href,
        width: w,
        height: h,
        preserveAspectRatio: "xMidYMid meet",
    })

    return image
}

function svgGroup(children: SVGElement | SVGElement[], transform?: string, className?: string): SVGGElement {
    const group = makeSvgElem("g", {
        class: className,
        transform,
    })
    children = Array.isArray(children) ? children : [children]
    for (const child of children) {
        group.appendChild(child)
    }
    return group
}

function astToSVG(ast: AST, clientWidth: number, background: string, id?: string): SVGSVGElement {
    const makeCenterCircle = (className?: string) =>
        // `<circle class="cp ${className ?? ""}" cx="0" cy="0" r="0.3" />`
        makeSvgElem("circle", {
            class: `cp ${className ?? ""}`,
            cx: 0,
            cy: 0,
            r: 0.3
        })

    function render(ast: AST): SVGElement {
        const svgElem = doRender()
        ast.svgElems.push(svgElem)
        return svgElem

        function doRender(): SVGElement {
            switch (ast._type) {
                case 'call':
                    return render(ast.def)
                case 'obj': {
                    const obj = ast.token
                    const def = ast.dialect.objectProps[obj]
                    const objSvg = def.makeSvg()
                    objSvg.setAttribute("transform", `translate(${-def.relativeCenter.x} ${-def.relativeCenter.y})`)
                    objSvg.classList.add("obj", obj)
                    if (ast.dialect.precolored) {
                        objSvg.classList.add("hascol")
                    }
                    return svgGroup([objSvg, makeCenterCircle()])
                }
                case 'pose': {
                    const poseCode = ast.dialect.poseProps[ast.token].poseCode
                    const firstSvg = render(ast.first)
                    const secondSvg = render(ast.second)
                    const combinedSize = sizeOf(ast)
                    const firstSize = sizeOf(ast.first)
                    const secondSize = sizeOf(ast.second)
                    switch (poseCode) {
                        case 'stack':
                            return svgGroup([
                                svgGroup(firstSvg, undefined, "first"),
                                svgGroup(secondSvg, undefined, "second"),
                            ], undefined, `pose stack`)

                        case 'bottom-align-left':
                        case 'bottom-align-right':
                        case 'bottom-align-junction':
                        case 'bottom-align-center': {
                            // TODO *-align-center
                            const combinedDy = firstSize.relativeCenter.y - combinedSize.relativeCenter.y
                            const [secondDx, combinedDx] = (() => {
                                switch (poseCode) {
                                    // TODO *-align-center
                                    case 'bottom-align-junction':
                                    case 'bottom-align-center':
                                        return [0, 0]
                                    case 'bottom-align-left': return [- firstSize.relativeCenter.x + secondSize.relativeCenter.x, firstSize.relativeCenter.x - combinedSize.relativeCenter.x]
                                    case 'bottom-align-right': {
                                        const combinedDx = firstSize.relativeCenter.x <= secondSize.relativeCenter.x ? 0 : firstSize.relativeCenter.x - combinedSize.relativeCenter.x
                                        return [firstSize.w - firstSize.relativeCenter.x - (secondSize.w - secondSize.relativeCenter.x), combinedDx]
                                    }
                                }
                            })()
                            const secondDy = firstSize.h - firstSize.relativeCenter.y + secondSize.relativeCenter.y

                            return svgGroup([
                                svgGroup(firstSvg, undefined, "first"),
                                svgGroup(
                                    svgGroup(secondSvg, `translate(${secondDx} ${secondDy})`),
                                    undefined, "second"),
                            ], `translate(${combinedDx} ${combinedDy})`, `pose bottom`)
                        }

                        case 'right-align-top':
                        case 'right-align-bottom':
                        case 'right-align-junction':
                        case 'right-align-center': {
                            // TODO *-align-center
                            const combinedDx = firstSize.relativeCenter.x - combinedSize.relativeCenter.x
                            const [secondDy, combinedDy] = (() => {
                                switch (poseCode) {
                                    // TODO *-align-center
                                    case 'right-align-junction':
                                    case 'right-align-center':
                                        return [0, 0]
                                    case 'right-align-top': return [- firstSize.relativeCenter.y + secondSize.relativeCenter.y, firstSize.relativeCenter.y - combinedSize.relativeCenter.y]
                                    case 'right-align-bottom': {
                                        const combinedDy = firstSize.relativeCenter.y > secondSize.relativeCenter.y ? 0 : -firstSize.relativeCenter.y + combinedSize.relativeCenter.y
                                        return [firstSize.h - firstSize.relativeCenter.y - (secondSize.h - secondSize.relativeCenter.y), combinedDy]
                                    }
                                }
                            })()
                            const secondDx = firstSize.w - firstSize.relativeCenter.x + secondSize.relativeCenter.x
                            return svgGroup([
                                svgGroup(firstSvg, undefined, "first"),
                                svgGroup(
                                    svgGroup(secondSvg, `translate(${secondDx} ${secondDy})`),
                                    undefined, "second"),
                            ], `translate(${combinedDx} ${combinedDy})`, `pose right`)
                        }
                    }
                }
                case 'rot': {
                    const angle = rotationTokenToDegrees(ast.dialect, ast.token)
                    return svgGroup(render(ast.arg), `rotate(${angle})`, `rot${ast.token}`)
                }
                case 'size': {
                    const size = ast.token
                    const factor = ast.dialect.sizeProps[size]
                    return svgGroup(render(ast.arg), `scale(${factor})`, `size${size}`)
                }
                case 'col': {
                    const color = ast.token
                    const cssColor = ast.dialect.colorProps[color]
                    const groupContent = render(ast.arg)
                    groupContent.querySelectorAll(".obj").forEach(elem => {
                        elem.classList.add("hascol")
                        if (elem instanceof SVGElement) {
                            elem.style.setProperty("--fillcolor", cssColor)
                        }
                    })
                    const group = svgGroup(groupContent, undefined, `col${color}`)
                    group.style.setProperty("--fillcolor", cssColor)
                    return group
                }
            }
        }
    }

    const debug = false
    const size = sizeOf(ast)
    const globalDx = size.relativeCenter.x
    const globalDy = size.relativeCenter.y
    const debugFrame = !debug ? makeSvgEmpty()
        : svgGroup([
            makeCenterCircle("orig"),
            makeSvgElem("rect", {
                x: -size.relativeCenter.x,
                y: -size.relativeCenter.y,
                width: size.w,
                height: size.h,
                fill: "none",
                stroke: "blue",
                "stroke-width": 0.1,
            })
        ])

    const svgPadding = 2
    const svg = makeSvgElem("svg", {
        id,
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: `-${svgPadding} -${svgPadding} ${size.w + 2 * svgPadding} ${size.h + 2 * svgPadding}`,
        width: clientWidth,
        style: `background: ${background}; margin: 0 auto;`
    })

    if (debug) {
        svg.querySelectorAll(".cp").forEach(elem => elem.classList.add("debug"))
    }


    const svgChildren = [
        svgGroup(render(ast), `translate(${globalDx} ${globalDy})`, "shadow"),
        debugFrame,
    ]

    for (const child of svgChildren) {
        svg.appendChild(child)
    }

    return svg
}

function children(ast: AST): AST[] {
    switch (ast._type) {
        case 'call':
        case 'obj':
            return []
        case 'pose':
            return [ast.first, ast.second]
        case 'rot':
        case 'size':
        case 'col':
            return [ast.arg]
    }
}

class EarlyReturn extends Error {
    constructor() {
        super("EarlyReturnError")
        this.name = "EarlyReturnError"
    }
}

function visit(ast: AST, fn: (ast: AST, depth: number) => boolean | void): void {
    try {
        doVisit(ast, 0)
    } catch (e) {
        if (!(e instanceof EarlyReturn)) {
            throw e
        }
    }

    function doVisit(ast: AST, depth: number): void {
        if (fn(ast, depth) === false) {
            throw new EarlyReturn()
        }

        for (const child of children(ast)) {
            doVisit(child, depth + 1)
        }
    }
}


function printSizes(ast: AST): void {
    visit(ast, (node, depth) => {
        const size = sizeOf(node)
        console.log(`${"  ".repeat(depth)}${pretty(node)}   -->   w=${size.w.toFixed(2)}, h=${size.h.toFixed(2)}, center=(${size.relativeCenter.x.toFixed(2)}, ${size.relativeCenter.y.toFixed(2)})`)
    })
}

function getSelectionOffsetsWithin(container: HTMLElement): { start: number, end: number } | undefined {
    const selection = document.getSelection()
    if (!selection || selection.rangeCount === 0) {
        return
    }
    const range = selection.getRangeAt(0)
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
        return
    }

    const startRange = document.createRange()
    startRange.selectNodeContents(container)
    startRange.setEnd(range.startContainer, range.startOffset)
    const start = startRange.toString().length

    const endRange = document.createRange()
    endRange.selectNodeContents(container)
    endRange.setEnd(range.endContainer, range.endOffset)
    const end = endRange.toString().length

    return { start, end }
}

function resolveTextPosition(container: HTMLElement, offset: number): { node: Node, offset: number } {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    let remaining = offset
    let lastTextNode: Text | undefined = undefined

    while (walker.nextNode()) {
        const textNode = walker.currentNode as Text
        lastTextNode = textNode
        const len = textNode.nodeValue?.length ?? 0
        if (remaining <= len) {
            return { node: textNode, offset: remaining }
        }
        remaining -= len
    }

    if (lastTextNode) {
        return { node: lastTextNode, offset: lastTextNode.nodeValue?.length ?? 0 }
    }

    return { node: container, offset: 0 }
}

function restoreSelectionOffsetsWithin(container: HTMLElement, start: number, end: number): void {
    const textLength = container.innerText.length
    const safeStart = Math.max(0, Math.min(start, textLength))
    const safeEnd = Math.max(0, Math.min(end, textLength))

    const startPos = resolveTextPosition(container, safeStart)
    const endPos = resolveTextPosition(container, safeEnd)
    const range = document.createRange()
    range.setStart(startPos.node, startPos.offset)
    range.setEnd(endPos.node, endPos.offset)

    const selection = document.getSelection()
    if (!selection) {
        return
    }
    selection.removeAllRanges()
    selection.addRange(range)
}

function parseNumericAttr(value: string | null): number | undefined {
    if (!value) return undefined
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

function svgRasterSize(svg: SVGSVGElement): { width: number, height: number } {
    const viewBox = svg.viewBox.baseVal
    const widthFromViewBox = viewBox && viewBox.width > 0 ? viewBox.width : undefined
    const heightFromViewBox = viewBox && viewBox.height > 0 ? viewBox.height : undefined
    const width = widthFromViewBox ?? parseNumericAttr(svg.getAttribute("width")) ?? 128
    const height = heightFromViewBox ?? parseNumericAttr(svg.getAttribute("height")) ?? 128
    return {
        width: Math.max(1, Math.round(width)),
        height: Math.max(1, Math.round(height)),
    }
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error("Unable to encode canvas as PNG"))
                return
            }
            resolve(blob)
        }, "image/png")
    })
}

function copyPngDataUrlWithExecCommand(pngDataUrl: string): boolean {
    const host = document.createElement("div")
    host.contentEditable = "true"
    setStyle(host, {
        position: "fixed",
        left: "-99999px",
        top: "0",
        opacity: "0",
    })

    const img = document.createElement("img")
    img.src = pngDataUrl
    img.alt = ""
    host.appendChild(img)
    document.body.appendChild(host)

    const range = document.createRange()
    range.selectNode(img)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)

    let copied = false
    try {
        copied = document.execCommand("copy")
    } catch {
        copied = false
    }

    selection?.removeAllRanges()
    document.body.removeChild(host)
    return copied
}

function triggerPngDownload(pngBlob: Blob, fileName = "screenshot.png"): void {
    const url = URL.createObjectURL(pngBlob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

async function copySvgToClipboardAsImage(svg: SVGSVGElement, svgStyleElem: Element): Promise<void> {
    const styleContent = svgStyleElem.textContent ?? ""
    const styleElem = makeSvgElem("style", {})
    styleElem.textContent = styleContent
    svg.insertBefore(styleElem, svg.firstChild)
    const serialized = new XMLSerializer().serializeToString(svg)
    svg.removeChild(styleElem)

    const svgBlob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" })
    let { width, height } = svgRasterSize(svg)
    width *= 100
    height *= 100

    const imageUrl = URL.createObjectURL(svgBlob)
    try {
        const image = new Image()
        const imageLoaded = new Promise<void>((resolve, reject) => {
            image.onload = () => resolve()
            image.onerror = () => reject(new Error("Failed to load SVG for rasterization"))
        })
        image.src = imageUrl
        await imageLoaded

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const context = canvas.getContext("2d")
        if (!context) {
            throw new Error("2D canvas context is unavailable")
        }
        context.drawImage(image, 0, 0, width, height)

        const pngBlob = await canvasToPngBlob(canvas)

        // Preferred path for browsers that support image writes (Chromium, newer Safari).
        if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        "image/png": pngBlob,
                        "image/svg+xml": svgBlob,
                    })
                ])
                return
            } catch {
                // Fall through to Safari legacy fallback below.
            }
        }

        // Safari fallback: select an <img> and use legacy copy command.
        const pngDataUrl = canvas.toDataURL("image/png")
        if (copyPngDataUrlWithExecCommand(pngDataUrl)) {
            return
        }

        // Last resort: provide a PNG file so user can still use it immediately.
        triggerPngDownload(pngBlob)
        throw new Error("Clipboard image copy is unavailable; downloaded PNG instead")
    } finally {
        URL.revokeObjectURL(imageUrl)
    }
}

function createSettingsPopup(currentDialect: Dialect, showCheatSheet: boolean, cheatSheetButtons: boolean, showPartialInput: boolean): void {

    const settingPopup = document.createElement("details")
    const settingPopupSummary = document.createElement("summary")
    settingPopupSummary.textContent = S("settings")
    settingPopup.appendChild(settingPopupSummary)
    setStyle(settingPopup, {
        position: "fixed",
        right: "16px",
        bottom: "16px",
        zIndex: "10000",
        background: "#fff",
        border: "1px solid #b7b7b7",
        borderRadius: "8px",
        boxShadow: "0 8px 24px rgb(0 0 0 / 0.16)",
        padding: "6px 8px",
        font: "500 12px/1.2 ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
    })
    setStyle(settingPopupSummary, {
        cursor: "pointer",
        listStyle: "none",
        userSelect: "none",
    })

    const settingPanel = document.createElement("div")
    setStyle(settingPanel, {
        marginTop: "8px",
        display: "grid",
        gap: "6px",
    })

    const makeSettingRow = (labelText: string, control: HTMLElement, controlFirst: boolean): HTMLElement => {
        const row = document.createElement("label")
        const text = document.createElement("span")
        text.textContent = controlFirst ? labelText : `${labelText}: `
        setStyle(row, {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
        })
        if (controlFirst) {
            row.appendChild(control)
            row.appendChild(text)
        } else {
            row.appendChild(text)
            row.appendChild(control)
        }
        return row
    }

    const langSelect = document.createElement("select")
    const currentLang = getCurrentLang()
    for (const langCode of Object.keys(Transations) as Lang[]) {
        const option = document.createElement("option")
        option.value = langCode
        const isCurrent = langCode === currentLang
        const label = isCurrent ? S(`langName_${langCode}`) : `${S(`langName_${langCode}`, langCode)} (${S(`langName_${langCode}`, currentLang)})`
        option.textContent = label
        option.selected = isCurrent
        langSelect.appendChild(option)
    }

    const dialectSelect = document.createElement("select")
    for (const dialectName of Object.keys(AllDialects)) {
        const option = document.createElement("option")
        option.value = dialectName
        option.textContent = dialectName
        option.selected = dialectName === currentDialect.name
        dialectSelect.appendChild(option)
    }

    const cheatSheetInput = document.createElement("input")
    cheatSheetInput.type = "checkbox"
    cheatSheetInput.checked = showCheatSheet

    const cheatSheetButtonsCheckbox = document.createElement("input")
    cheatSheetButtonsCheckbox.type = "checkbox"
    cheatSheetButtonsCheckbox.checked = cheatSheetButtons

    const showPartialInputCheckbox = document.createElement("input")
    showPartialInputCheckbox.type = "checkbox"
    showPartialInputCheckbox.checked = showPartialInput

    settingPanel.appendChild(makeSettingRow(S("uiLang"), langSelect, false))
    settingPanel.appendChild(makeSettingRow(S("dialect"), dialectSelect, false))
    settingPanel.appendChild(makeSettingRow(S("showCheatSheet"), cheatSheetInput, true))
    settingPanel.appendChild(makeSettingRow(S("cheatSheetButtons"), cheatSheetButtonsCheckbox, true))
    settingPanel.appendChild(makeSettingRow(S("showPartialInput"), showPartialInputCheckbox, true))
    settingPopup.appendChild(settingPanel)
    if (sessionStorage.getItem("openSettings") === "true") {
        settingPopup.open = true
        sessionStorage.removeItem("openSettings")
    }
    document.body.appendChild(settingPopup)

    const reloadWithSettings = (settings: { lang?: string, dialect?: string, showCheatSheet?: boolean, cheatSheetButtons?: boolean, showPartialInput?: boolean }): void => {
        const url = new URL(window.location.href)
        if (settings.lang) {
            url.searchParams.set("lang", settings.lang)
        }
        if (settings.dialect) {
            url.searchParams.set("dialect", settings.dialect)
        }
        if (settings.showCheatSheet === false) {
            url.searchParams.set("cheatsheet", "0")
        } else if (settings.showCheatSheet === true) {
            url.searchParams.delete("cheatsheet")
        }
        if (settings.cheatSheetButtons === false) {
            url.searchParams.set("cheatbuttons", "0")
        } else if (settings.cheatSheetButtons === true) {
            url.searchParams.delete("cheatbuttons")
        }
        if (settings.showPartialInput === true) {
            url.searchParams.set("partial", "1")
        } else if (settings.showPartialInput === false) {
            url.searchParams.delete("partial")
        }
        sessionStorage.setItem("openSettings", "true")
        window.location.assign(url.toString())
    }

    langSelect.addEventListener("change", () => {
        reloadWithSettings({ lang: langSelect.value })
    })
    dialectSelect.addEventListener("change", () => {
        reloadWithSettings({ dialect: dialectSelect.value })
    })
    cheatSheetInput.addEventListener("change", () => {
        reloadWithSettings({ showCheatSheet: cheatSheetInput.checked })
    })
    cheatSheetButtonsCheckbox.addEventListener("change", () => {
        reloadWithSettings({ cheatSheetButtons: cheatSheetButtonsCheckbox.checked })
    })
    showPartialInputCheckbox.addEventListener("change", () => {
        reloadWithSettings({ showPartialInput: showPartialInputCheckbox.checked })
    })
}

function makeCheatSheetContent(dialect: Dialect, useButtons: boolean): string {
    const smallSvgRender = (code: string): Element => {
        const asts = parse(code, dialect, false).asts
        const ast = asts.type === "full" ? asts.ast : asts.asts[0]
        const svg = astToSVG(ast, 20, background)
        svg.classList.add("objpreview")
        return svg
    }

    const unbreakableSpan = (content: string) => `<span style="white-space: nowrap;">${content}</span>`
    const sep = `<span style="font-size:120%; padding: 0 1ex;"> </span>`
    const asCheatSheetButton = (code: string, content: string, extraStyle = "") => {
        if (!useButtons) {
            return `<span style="${extraStyle}">${content}</span>`
        }
        return `<button type="button" class="cheatsheet-button" data-code="${code}" style="${extraStyle}">${content}</button>`
    }
    const colSpan = (colcode: string) => asCheatSheetButton(colcode, colcode, `padding: 1px; border: 3px solid ${dialect.colorProps[colcode.toLowerCase()]}`)
    const objSpan = (objcode: string) => asCheatSheetButton(objcode, `${objcode}${smallSvgRender(dialect.mkPreviewString(objcode)).outerHTML}`)
    const objs = Object.keys(dialect.objectProps).filter(obj => dialect.objectProps[obj].hidden !== true)

    const mkSection = (title: string, items: readonly string[], sectionSep: string, map: (item: string) => string = (item) => item): string => {
        if (items.length === 0) {
            return ""
        }
        return `<b>${title}:</b> ${items.map(item => map(capitalize(item))).join(sectionSep)}`
    }

    const cheatSheetSections = [[
        mkSection(S("shapes"), objs, " ", objSpan),
        mkSection(S("placement"), Object.entries(dialect.poseProps).filter(([_, v]) => v.primary === true).map(([k]) => k), " ", item => asCheatSheetButton(item, item)),
    ], [
        mkSection(S("colors"), dialect.allColors, " ", colSpan),
        mkSection(S("sizes"), dialect.allSizes, " ", item => asCheatSheetButton(item, item)),
        mkSection(S("rotation"),
            dialect.rotation === true
                ? ["45", "90", "180", "270", "etc."]
                : dialect.rotation
                    ? Object.entries(dialect.rotation).map(([token, degrees]) => `${token}`)
                    : [],
            " ",
            item => dialect.rotation === true ? item : asCheatSheetButton(item, item)),
    ]]

    return cheatSheetSections.map(subsection => subsection.map(unbreakableSpan).join(sep)).join("<br>")
}

function makeToastUI() {
    const toast = document.createElement("div")
    setStyle(toast, {
        position: "fixed",
        right: "16px",
        bottom: "76px",
        padding: "8px 12px",
        borderRadius: "8px",
        font: "600 12px/1.2 ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
        color: "#fff",
        background: "#333",
        boxShadow: "0 8px 24px rgb(0 0 0 / 0.2)",
        opacity: "0",
        transform: "translateY(8px)",
        transition: "opacity 120ms ease, transform 120ms ease",
        pointerEvents: "none",
        zIndex: "9999",
    })
    document.body.appendChild(toast)

    let toastTimeout: number | undefined
    const showToast = (message: string, kind: "success" | "error" = "success") => {
        toast.textContent = message
        toast.style.background = kind === "success" ? "#1d6e2c" : "#a12828"
        toast.style.opacity = "1"
        toast.style.transform = "translateY(0)"
        if (toastTimeout !== undefined) {
            window.clearTimeout(toastTimeout)
        }
        toastTimeout = window.setTimeout(() => {
            toast.style.opacity = "0"
            toast.style.transform = "translateY(8px)"
        }, 1400)
    }
    return showToast
}


async function main() {

    const codeContainer = document.getElementById("code-container")
    const cheatSheetContainer = document.getElementById("cheatsheet-container")
    const svgContainer = document.getElementById("svg-container")
    const prettyprintContainer = document.getElementById("prettyprint-container")
    const svgStyleElem = document.querySelector("style#forsvg")

    if (!codeContainer || !cheatSheetContainer || !svgContainer || !prettyprintContainer || !svgStyleElem) {
        throw new Error("HTML containers not found")
    }

    const initialUrlParams = new URLSearchParams(window.location.search)
    const dialectNameParam = initialUrlParams.get("dialect")
    const langParam = initialUrlParams.get("lang")
    const showCheatSheet = initialUrlParams.get('cheatsheet') !== '0'
    const cheatSheetButtons = initialUrlParams.get('cheatbuttons') !== '0'
    const showPartialInput = initialUrlParams.get('partial') === '1'
    trySetCurrentLang(langParam)
    const dialect = dialectNameParam !== null && dialectNameParam in AllDialects ? AllDialects[dialectNameParam] : DialectDefault

    createSettingsPopup(dialect, showCheatSheet, cheatSheetButtons, showPartialInput)

    const setParseStatus = (status: "idle" | "ok" | "error") => {
        switch (status) {
            case "idle":
                codeContainer.style.removeProperty("background")
                break
            case "ok":
                codeContainer.style.setProperty("background", "#f0fff0")
                break
            case "error":
                codeContainer.style.setProperty("background", "#fff0f0")
                break
        }
    }

    setParseStatus("idle")

    const showToast = makeToastUI()

    document.addEventListener("keydown", (event) => {
        const isCopyRenderedSvgShortcut = event.metaKey
            && event.shiftKey
            && !event.ctrlKey
            && !event.altKey
            && event.key.toLowerCase() === "c"

        if (!isCopyRenderedSvgShortcut) {
            return
        }

        const renderedSvg = svgContainer.querySelector("svg#main")
        if (!(renderedSvg instanceof SVGSVGElement)) {
            showToast("No SVG to copy", "error")
            return
        }

        event.preventDefault()
        void copySvgToClipboardAsImage(renderedSvg, svgStyleElem)
            .then(() => {
                showToast("SVG copied as image")
            })
            .catch((error) => {
                console.error("Could not copy rendered SVG to clipboard", error)
                showToast("Copy failed", "error")
            })
    })

    cheatSheetContainer.innerHTML = makeCheatSheetContent(dialect, cheatSheetButtons)
    cheatSheetContainer.classList.toggle("buttons-enabled", cheatSheetButtons)

    let currentAst: AST | undefined = undefined

    const selectTokenInCode = (tokenElem: HTMLElement): void => {
        if (!codeContainer.contains(tokenElem)) {
            return
        }
        const range = document.createRange()
        range.selectNodeContents(tokenElem)

        const selection = document.getSelection()
        if (!selection) {
            return
        }
        selection.removeAllRanges()
        selection.addRange(range)
        codeContainer.focus()
    }

    const findAstGeneratingSvgElem = (svgElem: SVGElement): AST | undefined => {
        if (!currentAst) {
            return
        }

        let bestMatchAst: AST | undefined = undefined
        let bestMatchDepth = -1

        visit(currentAst, (node, depth) => {
            const containsClickedShape = node.svgElems.some(nodeSvgElem =>
                nodeSvgElem === svgElem || nodeSvgElem.contains(svgElem)
            )
            if (!containsClickedShape) {
                return
            }
            if (depth >= bestMatchDepth) {
                bestMatchDepth = depth
                bestMatchAst = node
            }
        })

        return bestMatchAst
    }

    document.addEventListener("pointerup", e => {
        if ((e.target instanceof SVGElement) && e.target.classList.contains("obj")) {
            const generatingAst = findAstGeneratingSvgElem(e.target)
            if (!generatingAst) {
                return
            }
            e.preventDefault()
            e.stopPropagation()
            selectTokenInCode(generatingAst.srcElem)
        }
    }, { capture: true })

    codeContainer.addEventListener("input", () => {
        renderAndShow(codeContainer.innerText, true)
    })
    codeContainer.addEventListener('pointerdown', e => {
        if (e.detail !== 2) {
            return
        }
        const sel = window.getSelection()
        if (!sel || !(e.target instanceof HTMLElement)) return
        // e.preventDefault()
        // e.stopPropagation()
        sel.selectAllChildren(e.target)
    }, { capture: true })

    if (cheatSheetButtons) {
        cheatSheetContainer.addEventListener("pointerdown", e => {
            if (e.target instanceof Element && e.target.closest(".cheatsheet-button")) {
                e.preventDefault()
            }
        })
    }

    const ArgKeys = ["arg", "first", "second"] as const
    type ArgType = typeof ArgKeys[number]

    const findDependentHighlights = (span: HTMLElement): { svgElems: SVGElement[], args: [HTMLElement, ArgType][], builtObj: HTMLElement | undefined } => {
        const ast = elemToAstMap.get(span)
        if (!ast) return { svgElems: [], args: [], builtObj: undefined }

        // if we are a unary op, try to go down to the built object to find highlights
        let objAst: AST = ast
        while ("arg" in objAst) {
            objAst = objAst.arg
        }
        // revert if we are a pose, since poses are binary and highlight a move rather than children
        if (objAst._type === "pose") {
            objAst = ast
        }

        const svgElems = objAst.svgElems

        const args: [HTMLElement, ArgType][] = []
        for (const key of ArgKeys) {
            if (key in ast) {
                const argAst: AST = ast[key as keyof AST] as any
                args.push([argAst.builtObjElem, key])
            }
        }
        // only highlight built object if it's not the same as the current span
        const builtObj = args.length === 0 ? undefined : ast.builtObjElem
        return { svgElems, args, builtObj: builtObj }
    }

    let highlightedSpan: HTMLElement | undefined = undefined

    const clearHighlights = () => {
        if (highlightedSpan) {
            highlightedSpan.classList.remove("highlighted")
            const { svgElems, args, builtObj } = findDependentHighlights(highlightedSpan)
            for (const svgElem of svgElems) {
                svgElem.classList.remove("highlighted")
            }
            for (const [argElem, argType] of args) {
                argElem.classList.remove("highlighted_arg", argType)
            }
            if (builtObj) {
                builtObj.classList.remove("highlighted_built")
            }
            highlightedSpan = undefined
        }
    }

    document.addEventListener("selectionchange", () => {

        const isAtEndOfContainer = (): boolean => {
            const selectionOffsets = getSelectionOffsetsWithin(codeContainer)
            return selectionOffsets !== undefined
                && selectionOffsets.start === selectionOffsets.end
                && selectionOffsets.end === codeContainer.innerText.length
        }

        const findSelectionNode = (): HTMLElement | undefined => {
            const sel = document.getSelection()
            if (!sel) return
            if (isAtEndOfContainer()) return
            const node = sel.focusNode
            if (!node) return
            if (node.nodeType !== Node.TEXT_NODE) return
            const parent = node.parentElement
            if (!parent) return
            if (!codeContainer.contains(parent)) return
            if (!(parent instanceof HTMLElement)) return
            return parent
        }

        const newHighlightedSpan = findSelectionNode()
        // console.log("Selection changed, element:", newHighlightedSpan)

        if (newHighlightedSpan !== highlightedSpan) {
            clearHighlights()
            if (newHighlightedSpan) {
                newHighlightedSpan.classList.add("highlighted")
                const { svgElems, args, builtObj } = findDependentHighlights(newHighlightedSpan)
                for (const svgElem of svgElems) {
                    svgElem.classList.add("highlighted")
                }
                for (const [argElem, argType] of args) {
                    argElem.classList.add("highlighted_arg", argType)
                }
                if (builtObj) {
                    builtObj.classList.add("highlighted_built")
                }
            }
            highlightedSpan = newHighlightedSpan
        }
    })

    codeContainer.style.padding = "4px"
    svgContainer.style.background = background
    svgContainer.style.position = "relative"
    prettyprintContainer.style.background = background

    let lastRenderedString = ""
    let elemToAstMap: WeakMap<HTMLElement, AST> = new WeakMap()

    const renderAndShow = (code: string, saveToState: boolean): void => {
        if (code === lastRenderedString) {
            return
        }

        clearHighlights()
        const previousSelection = getSelectionOffsetsWithin(codeContainer)
        let parseResult: ParseResult | undefined = undefined

        if (code.trim() === "") {
            codeContainer.innerHTML = ""
            svgContainer.innerHTML = ""
            prettyprintContainer.innerHTML = ""
            currentAst = undefined
            elemToAstMap = new WeakMap()
            setParseStatus("idle")
        } else {

            try {
                parseResult = parse(code, dialect, showPartialInput)
            } catch (e) {
                if (e instanceof ParseError) {
                    console.log(`Parse error at position ${e.pos}: ${e.message}`)
                } else {
                    throw e
                }
            }
            if (parseResult) {
                const { asts, htmlElements, elemToAstMap: newElemToAstMap } = parseResult
                const mainAst = asts.type === "full" ? asts.ast : asts.asts[0]
                currentAst = mainAst
                elemToAstMap = newElemToAstMap
                setParseStatus(asts.type === "full" ? "ok" : "error")
                prettyprintContainer.innerHTML = pretty(mainAst)
                // printSizes(ast)
                const svg = astToSVG(mainAst, svgContainer.clientWidth, background, "main")
                svgContainer.innerHTML = ""
                svgContainer.appendChild(svg)

                codeContainer.innerHTML = ""
                for (const elem of htmlElements) {
                    codeContainer.appendChild(elem)
                }

                // show the other ASTs as "thought bubbles" at the right of the main SVG
                const auxiliaryAsts = asts.type === "full" ? [] : asts.asts.slice(1)
                if (auxiliaryAsts.length > 0) {
                    const mainRect = svg.getBoundingClientRect()
                    const mainHeight = mainRect.height || svg.viewBox.baseVal.height
                    const bubbleGap = 12
                    const bubbleWidth = Math.max(72, Math.min(160, svgContainer.clientWidth * 0.22))
                    const maxBubbleHeight = auxiliaryAsts.length === 1
                        ? mainHeight * 0.45
                        : Math.max(48, (mainHeight - bubbleGap * (auxiliaryAsts.length - 1)) / auxiliaryAsts.length - 16)
                    const maxMediumThoughtDotSize = maxBubbleHeight / 4
                    const maxSmallThoughtDotSize = maxMediumThoughtDotSize / 2
                    const containerRect = svgContainer.getBoundingClientRect()
                    const auxContainer = document.createElement("div")
                    Object.assign(auxContainer.style, {
                        position: "absolute",
                        top: `${Math.max(0, mainRect.top - containerRect.top + svgContainer.scrollTop)}px`,
                        right: "0",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        gap: `${bubbleGap}px`,
                        height: `${mainHeight}px`,
                        justifyContent: auxiliaryAsts.length === 1 ? "center" : "space-evenly",
                        padding: `8px ${maxMediumThoughtDotSize + maxSmallThoughtDotSize}px 8px 12px`,
                        pointerEvents: "none",
                    })

                    const bubbleBorder = "0.5px solid #333"
                    const wiggleAmplitude = 20
                    const wiggleOffset = () => `${(Math.random() * wiggleAmplitude - wiggleAmplitude / 2).toFixed(1)}px`
                    for (let i = 0; i < auxiliaryAsts.length; i++) {
                        const bubble = document.createElement("div")
                        bubble.classList.add("aux-thought-bubble")
                        Object.assign(bubble.style, {
                            position: "relative",
                            boxSizing: "border-box",
                            width: `${bubbleWidth}px`,
                            maxHeight: `${maxBubbleHeight}px`,
                            padding: "10px",
                            border: bubbleBorder,
                            borderRadius: "20%",
                            background: darkerBackground,
                            boxShadow: "2px 3px 0 rgb(0 0 0 / 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            animationDuration: `${7 + Math.random() * 4}s`,
                            animationDelay: `${-Math.random() * 6}s`,
                        })
                        for (let k = 1; k <= 3; k++) {
                            bubble.style.setProperty(`--wiggle-x${k}`, wiggleOffset())
                            bubble.style.setProperty(`--wiggle-y${k}`, wiggleOffset())
                        }

                        const auxSvg = astToSVG(auxiliaryAsts[i], bubbleWidth - 16, darkerBackground, `aux-${i}`)
                        const viewBox = auxSvg.viewBox.baseVal
                        const actualBubbleHeight = Math.min(
                            maxBubbleHeight,
                            (bubbleWidth - 20) * viewBox.height / viewBox.width + 20
                        )
                        const mediumThoughtDotSize = actualBubbleHeight / 4
                        const smallThoughtDotSize = mediumThoughtDotSize / 2

                        const mediumThoughtDot = document.createElement("div")
                        Object.assign(mediumThoughtDot.style, {
                            position: "absolute",
                            right: `${-mediumThoughtDotSize * 0.55}px`,
                            top: "20%",
                            width: `${mediumThoughtDotSize}px`,
                            height: `${mediumThoughtDotSize}px`,
                            background: darkerBackground,
                            border: bubbleBorder,
                            borderRadius: "50%",
                            boxSizing: "border-box",
                            transform: "translateY(-50%)",
                        })
                        bubble.appendChild(mediumThoughtDot)

                        const smallThoughtDot = document.createElement("div")
                        Object.assign(smallThoughtDot.style, {
                            position: "absolute",
                            right: `${-(mediumThoughtDotSize - smallThoughtDotSize * 0.4)}px`,
                            top: "18%",
                            width: `${smallThoughtDotSize}px`,
                            height: `${smallThoughtDotSize}px`,
                            background: darkerBackground,
                            border: bubbleBorder,
                            borderRadius: "50%",
                            boxSizing: "border-box",
                            transform: "translateY(-50%)",
                        })
                        bubble.appendChild(smallThoughtDot)

                        auxSvg.style.margin = "0"
                        auxSvg.style.maxWidth = "100%"
                        auxSvg.style.maxHeight = `${maxBubbleHeight - 16}px`
                        bubble.appendChild(auxSvg)
                        auxContainer.appendChild(bubble)
                    }

                    svgContainer.appendChild(auxContainer)
                }

            } else {
                currentAst = undefined
                elemToAstMap = new WeakMap()
                setParseStatus("error")
                codeContainer.innerHTML = ""
                codeContainer.appendChild(fallbackParse(code))
            }
        }

        if (previousSelection) {
            restoreSelectionOffsetsWithin(codeContainer, previousSelection.start, previousSelection.end)
        }

        lastRenderedString = code
        if (saveToState) {
            const isValidCode = parseResult !== undefined
            saveToURL(code, isValidCode)
        }
    }

    if (cheatSheetButtons) {
        cheatSheetContainer.addEventListener("click", e => {
            const button = e.target instanceof Element ? e.target.closest(".cheatsheet-button") : null
            if (!(button instanceof HTMLElement)) {
                return
            }
            const code = button.dataset.code
            if (code === undefined) {
                return
            }

            e.preventDefault()
            const currentCode = codeContainer.innerText
            const selectionOffsets = getSelectionOffsetsWithin(codeContainer) ?? { start: currentCode.length, end: currentCode.length }
            const start = Math.min(selectionOffsets.start, selectionOffsets.end)
            const end = Math.max(selectionOffsets.start, selectionOffsets.end)
            const updatedCode = `${currentCode.slice(0, start)}${code}${currentCode.slice(end)}`
            const caretOffset = start + code.length

            renderAndShow(updatedCode, true)
            restoreSelectionOffsetsWithin(codeContainer, caretOffset, caretOffset)
            codeContainer.focus({ preventScroll: true })
        })
    }

    let saveToURL: (code: string, isValidCode: boolean) => void
    let lastSavedWasValid = true

    saveToURL = (code, isValidCode) => {
        const url = new URL(window.location.href)
        if (code.trim().length === 0) {
            url.searchParams.delete('t')
        } else {
            const compressed = LZString.compressToEncodedURIComponent(code)
            url.searchParams.set('t', compressed)
        }
        const usePush = !isValidCode && lastSavedWasValid
        const method = usePush ? "pushState" : "replaceState"
        // console.log("saving", { valid: isValidCode, method, code })
        window.history[method]({ code }, '', url.toString())
        lastSavedWasValid = isValidCode
    }

    window.addEventListener("popstate", (event) => {
        if (event.state && typeof event.state.code === "string") {
            console.log("Popstate event, loading code from state:", event.state.code)
            renderAndShow(event.state.code, false)
        }
    })

    if (!showCheatSheet) {
        cheatSheetContainer.style.display = "none"
    }

    const loadFromURL = (): boolean => {
        const compressed = initialUrlParams.get('t')
        if (compressed === null) return false
        const decompressed = LZString.decompressFromEncodedURIComponent(compressed)
        if (decompressed === null) return false
        renderAndShow(decompressed, true)
        return true
    }

    if (!loadFromURL()) {
        renderAndShow(codeContainer.innerText, false)
    }

    codeContainer.focus({ preventScroll: true })
}

document.addEventListener("DOMContentLoaded", main)
